/**
 * alteration-cart-sync.js
 *
 * Keeps parent and child alteration line items in sync:
 * - Parent removed/quantity changed → child is removed/updated
 * - Child removed → parent's alteration properties are stripped
 */

const ALTERATION_PROPS = ['_Alteration ref', '_Has alteration', 'Alteration detail'];

function getSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids).join(',');
}

function dispatchCartUpdate(response) {
  document.dispatchEvent(
    new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: response,
        sourceId: 'alteration-cart-sync',
        data: { source: 'alteration-cart-sync', sections: response.sections },
      },
    })
  );
}

/**
 * Waits for the next cart:update event that wasn't dispatched by this script.
 * @param {Function} callback
 */
function waitForCartUpdate(callback) {
  function handler(e) {
    if (e.detail?.data?.source === 'alteration-cart-sync') return;
    document.removeEventListener('cart:update', handler);
    callback();
  }
  document.addEventListener('cart:update', handler);
}

function cartChange(body) {
  const sections = getSectionIds();
  return fetch(Theme.routes.cart_change_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ ...body, sections }),
  })
    .then((r) => r.text())
    .then((text) => JSON.parse(text));
}

/**
 * Finds the child alteration line item by Alteration ref and updates its quantity.
 * @param {string} alterationRef
 * @param {number} quantity
 */
function syncChildQuantity(alterationRef, quantity) {
  fetch('/cart.js')
    .then((r) => r.json())
    .then((cart) => {
      const idx = cart.items.findIndex(
        (item) => item.properties?.['_Alteration ref'] === alterationRef
          && !item.properties?.['_Has alteration']
      );
      if (idx === -1) return;

      return cartChange({ line: idx + 1, quantity })
        .then((response) => {
          if (response.errors) return;
          dispatchCartUpdate(response);
        });
    });
}

/**
 * Finds the parent line item by Alteration ref and strips its alteration properties.
 * @param {string} alterationRef
 */
function cleanParentProperties(alterationRef) {
  fetch('/cart.js')
    .then((r) => r.json())
    .then((cart) => {
      const item = cart.items.find(
        (i) => i.properties?.['_Alteration ref'] === alterationRef
          && i.properties?.['_Has alteration']
      );
      if (!item) return;

      const cleanedProperties = Object.fromEntries(
        Object.entries(item.properties).map(([key, value]) =>
          ALTERATION_PROPS.includes(key) ? [key, ''] : [key, value]
        )
      );

      return cartChange({ id: item.key, quantity: item.quantity, properties: cleanedProperties })
        .then((response) => {
          if (response.errors) return;
          // Dispatch without sections to force sectionRenderer to fetch fresh HTML
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: response,
              sourceId: 'alteration-cart-sync',
              data: { source: 'alteration-cart-sync' },
            },
          }));
        });
    });
}

// Handle remove button clicks
document.addEventListener('click', (e) => {
  const removeBtn = e.target.closest('.cart-items__remove');
  if (!removeBtn) return;

  const row = removeBtn.closest('.cart-items__table-row');
  if (!row?.dataset.alterationRef) return;

  const alterationRef = row.dataset.alterationRef;
  const role = row.dataset.alterationRole;

  if (role === 'parent') {
    waitForCartUpdate(() => syncChildQuantity(alterationRef, 0));
  } else if (role === 'child') {
    row.remove();
    const childLine = parseInt(row.dataset.lineIndex, 10);
    cartChange({ line: childLine, quantity: 0 })
      .then((response) => {
        if (response.errors) return;
        dispatchCartUpdate(response);
        cleanParentProperties(alterationRef);
      });
  }
});

// Handle quantity changes via the quantity selector (parent only)
document.addEventListener('quantity-selector:update', (e) => {
  const { quantity, cartLine: line } = e.detail;
  if (!line) return;

  const row = document.querySelector(`.cart-items__table-row[data-line-index="${line}"]`);
  if (!row?.dataset.alterationRef || row.dataset.alterationRole !== 'parent') return;

  const alterationRef = row.dataset.alterationRef;

  waitForCartUpdate(() => syncChildQuantity(alterationRef, quantity));
});
